import React, { useState, useEffect, useCallback } from 'react';
import { syncApi } from '../../services/api';
import {
  Eye, Play, Loader, CheckCircle, XCircle, Clock,
  ChevronDown, ChevronRight, RotateCcw
} from 'lucide-react';

const ViewSyncPage = () => {
  const [targets, setTargets]               = useState([]);
  const [selectedTarget, setSelectedTarget] = useState(null);
  const [history, setHistory]               = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [expandedHistory, setExpandedHistory] = useState(false);

  const [syncing, setSyncing]     = useState(false);
  const [syncResult, setSyncResult] = useState(null);

  // ── Data loading ───────────────────────────────────────────────────
  const loadHistory = useCallback(async (target) => {
    if (!target) return;
    setHistoryLoading(true);
    try {
      const res = await syncApi.getViewHistory(target);
      setHistory(res.data || []);
    } catch {
      setHistory([]);
    }
    setHistoryLoading(false);
  }, []);

  useEffect(() => {
    syncApi.getViewTargets()
      .then(res => setTargets(res.data || []))
      .catch(() => setTargets([]));
  }, []);

  useEffect(() => {
    if (selectedTarget) loadHistory(selectedTarget);
  }, [selectedTarget, loadHistory]);

  // ── Actions ────────────────────────────────────────────────────────
  const runSync = async () => {
    if (!selectedTarget || syncing) return;
    setSyncing(true);
    setSyncResult(null);
    try {
      const res = await syncApi.syncViews(selectedTarget);
      setSyncResult({ success: true, data: res.data });
      loadHistory(selectedTarget);
    } catch (err) {
      setSyncResult({ success: false, error: err.response?.data?.error || err.message });
    }
    setSyncing(false);
  };

  const acknowledge = () => setSyncResult(null);

  // ── Helpers ────────────────────────────────────────────────────────
  const formatDate = (dateStr) => {
    if (!dateStr) return '--';
    const d = new Date(dateStr);
    return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDuration = (seconds) => {
    if (!seconds && seconds !== 0) return '--';
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return m > 0 ? `${m}m ${s}s` : `${s}s`;
  };

  const isComplete      = !!syncResult;
  const showExecuteTile = !!selectedTarget;
  const showResultsTile = isComplete && syncResult.success;
  const hasFailures     = syncResult?.data?.totalFail > 0;

  // ── Render ─────────────────────────────────────────────────────────
  return (
    <div className="dev-module sync-dashboard-page">
      <div className="dev-module-header">
        <div className="dev-module-header-left">
          <h1>
            <Eye size={20} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
            View Synchronization
          </h1>
          {selectedTarget && (
            <span className="dev-module-meta">TMPLT → {selectedTarget}</span>
          )}
        </div>
      </div>

      <div className="dev-module-content">
        <div className="sync-steps-layout">

          {/* ═══════════ TILE 1: SETUP ═══════════ */}
          <div className="stui-step-panel sync-setup-panel">
            <div className="stui-step-header">
              <span className="stui-step-number">1</span>
              <h3>Setup</h3>
            </div>
            <p className="stui-step-desc">
              Select target database. Views always sync from <strong>Preprod_TMPLT</strong> (master).
            </p>

            <div className="sync-db-selector">
              {targets.map(t => (
                <button
                  key={t.connStringName}
                  className={`sync-db-btn ${selectedTarget === t.connStringName ? 'active' : ''}`}
                  onClick={() => { setSelectedTarget(t.connStringName); setSyncResult(null); }}
                  disabled={syncing}
                >
                  <span className="sync-db-label">{t.displayName}</span>
                </button>
              ))}
              {targets.length === 0 && (
                <span style={{ fontSize: '12px', color: '#888' }}>Loading targets...</span>
              )}
            </div>

            {selectedTarget && (
              <div className="sync-history-section">
                <button
                  className="sync-history-toggle"
                  onClick={() => setExpandedHistory(!expandedHistory)}
                >
                  {expandedHistory ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  <Clock size={14} />
                  History {historyLoading ? '...' : `(${history.length})`}
                </button>

                {expandedHistory && history.length > 0 && (
                  <div className="sync-history-table-wrap">
                    <table className="sync-history-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th className="center">Status</th>
                          <th className="right">Views</th>
                          <th className="right">Time</th>
                        </tr>
                      </thead>
                      <tbody>
                        {history.slice(0, 10).map((h, i) => (
                          <tr key={i}>
                            <td>{formatDate(h.started_At)}</td>
                            <td className="center">
                              <span className={`sync-status-badge ${h.status?.toLowerCase()}`}>
                                {h.status}
                              </span>
                            </td>
                            <td className="right">{h.objects_Applied ?? '--'}</td>
                            <td className="right">{formatDuration(h.duration_Sec)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {expandedHistory && history.length === 0 && !historyLoading && (
                  <p className="sync-no-history">No view sync history for {selectedTarget}.</p>
                )}
              </div>
            )}
          </div>

          {/* ═══════════ TILE 2: EXECUTE ═══════════ */}
          <div className={`stui-step-panel sync-execute-panel ${syncing ? 'running' : ''}`}>
            {showExecuteTile && (
              <>
                <div className="stui-step-header">
                  <span className="stui-step-number">2</span>
                  <h3>Execute</h3>
                </div>

                {!syncing && !isComplete && (
                  <>
                    <p className="stui-step-desc">
                      Push all views from <strong>TMPLT</strong> to <strong>{selectedTarget}</strong>.
                    </p>
                    <button className="import-execute-btn" onClick={runSync}>
                      <Play size={16} />
                      Sync Views
                    </button>
                  </>
                )}

                {syncing && (
                  <div className="sync-running-status">
                    <div className="sync-running-header">
                      <Loader size={18} className="spinning" />
                      <span>Syncing views to {selectedTarget}...</span>
                    </div>
                  </div>
                )}

                {isComplete && (
                  <div className={`sync-complete-brief ${!syncResult.success || hasFailures ? 'failed' : 'success'}`}>
                    {!syncResult.success || hasFailures
                      ? <XCircle size={18} className="icon-fail" />
                      : <CheckCircle size={18} className="icon-success" />}
                    <div>
                      <div className="sync-complete-label">
                        {!syncResult.success
                          ? 'View Sync Failed'
                          : hasFailures
                            ? `View Sync — ${syncResult.data.totalFail} failure(s)`
                            : 'View Sync Complete'}
                      </div>
                      {syncResult.success && (
                        <div className="sync-complete-stats">
                          {syncResult.data.totalSuccess} of {syncResult.data.viewCount} views synced
                        </div>
                      )}
                      {!syncResult.success && (
                        <div className="sync-complete-stats">{syncResult.error}</div>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* ═══════════ TILE 3: RESULTS ═══════════ */}
          <div className="stui-step-panel sync-validation-panel">
            {showResultsTile && (
              <>
                <div className="stui-step-header">
                  <span className="stui-step-number">3</span>
                  <h3>Results</h3>
                </div>

                <div className={`sync-val-result ${hasFailures ? 'failed' : 'success'}`}>
                  <div className="sync-val-summary">
                    {hasFailures ? (
                      <><XCircle size={20} /><span>{syncResult.data.totalFail} view(s) failed — see below</span></>
                    ) : (
                      <><CheckCircle size={20} /><span>{syncResult.data.viewCount} views synced to {syncResult.data.target}</span></>
                    )}
                  </div>
                </div>

                <div className="sync-history-table-wrap" style={{ marginTop: '10px' }}>
                  <table className="sync-history-table">
                    <thead>
                      <tr>
                        <th>View</th>
                        <th className="center">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {syncResult.data.results.map((r, i) => (
                        <tr key={i}>
                          <td>{r.name}</td>
                          <td className="center" title={r.error || ''}>
                            {r.success
                              ? <CheckCircle size={14} className="icon-success" />
                              : <XCircle size={14} className="icon-fail" />}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <button className="sync-acknowledge-btn" onClick={acknowledge}>
                  <RotateCcw size={16} />
                  Acknowledge &amp; Reset
                </button>
              </>
            )}
          </div>

        </div>
      </div>

      <style>{`
        .spinning { animation: spin 1s linear infinite; }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

export default ViewSyncPage;
